/**
 * Diagnostic Question Generator
 * Generates state-specific diagnostic questions from templates
 */

import type {
  DiagnosticQuestionConfig,
  StateRequirements,
  QuestionTemplate,
  SubQuestion,
  ScoringRule,
  NestedCondition,
  ReportLanguage,
} from '../../types/diagnostic';
import { getStateByCode } from '../states/all-states';
import { getTemplatesForState } from './templates';
import { getStateSpecificQuestions, hasStateSpecificQuestions } from './states';

// ============================================
// STATE REQUIREMENTS EXTRACTOR
// ============================================

/**
 * Extract state requirements from the state regulatory data
 */
export function extractStateRequirements(stateCode: string): StateRequirements | null {
  const state = getStateByCode(stateCode);
  if (!state) return null;

  // Parse insurance minimum (e.g., "$1,000,000" -> "$1,000,000")
  const insuranceMin = state.requirements.insuranceMinimum;

  // Determine record retention years based on state
  // Most states are 6 years, California is 7
  const retentionYears = stateCode === 'CA' ? 7 : 6;

  // Determine license renewal period
  // Most states are 1-2 years, varies by state
  const renewalYears = getLicenseRenewalYears(stateCode);

  return {
    stateCode: state.code,
    stateName: state.name,
    agencyName: state.regulatoryBody,
    agencyAbbr: state.regulatoryBodyAbbr,
    agencyUrl: state.websiteUrl,
    agencyPhone: getAgencyPhone(stateCode),

    // License
    licenseRenewalYears: renewalYears,
    licenseRenewalWarningDays: renewalYears === 2 ? 120 : 90,

    // Insurance
    liabilityMinimum: insuranceMin,
    liabilityPerOccurrence: stateCode === 'CA' ? '$1,000,000' : undefined,
    liabilityAggregate: stateCode === 'CA' ? '$3,000,000' : undefined,
    workersCompRequired: stateCode === 'CA',
    dishonestyBondRequired: stateCode === 'CA' || stateCode === 'CT' || stateCode === 'FL',
    dishonestyBondMinimum: stateCode === 'CA' ? 10000 : stateCode === 'CT' ? 10000 : undefined,

    // Training
    entryLevelTrainingHours: state.training.initialHours,
    annualTrainingHours: state.training.annualHours,
    specificTrainingRequirements: state.training.specialRequirements,

    // Background checks
    backgroundCheckType: state.backgroundCheck.type,
    backgroundCheckFrequencyYears: parseBackgroundFrequency(state.backgroundCheck.frequency),
    liveScanRequired: stateCode === 'CA',
    registryRequired: stateCode === 'CA' || state.backgroundCheck.registryChecks.some(r =>
      r.toLowerCase().includes('registry') || r.toLowerCase().includes('aide')
    ),
    registryName: stateCode === 'CA' ? 'CDSS Home Care Aide Registry' : undefined,
    registryUrl: stateCode === 'CA' ? 'https://www.ccld.dss.ca.gov/hcsregistry' : undefined,

    // Health requirements
    tbTestRequired: state.requirements.tbScreening,
    tbTestFrequencyYears: state.requirements.tbScreening ? 2 : undefined,

    // Records
    recordRetentionYears: retentionYears,

    // Regulations
    primaryRegulation: Object.values(state.regulatoryReferences)[0] || '',
    regulationName: `${state.name} Home Care Regulations`,

    // Special requirements
    specialRequirements: state.keyRegulations,
  };
}

function getLicenseRenewalYears(stateCode: string): number {
  // States with 2-year renewal
  const twoYearStates = ['CA', 'AZ', 'CO', 'IN', 'MD', 'MO', 'MT', 'NE', 'NH', 'NJ', 'OR', 'RI', 'SC', 'TN', 'UT', 'DC'];
  // States with 3-year renewal
  const threeYearStates = ['VA'];

  if (twoYearStates.includes(stateCode)) return 2;
  if (threeYearStates.includes(stateCode)) return 3;
  return 1; // Default to annual
}

function parseBackgroundFrequency(frequency: string): number {
  const match = frequency.match(/(\d+)/);
  return match ? parseInt(match[1]) : 3;
}

function getAgencyPhone(stateCode: string): string | undefined {
  const phones: Record<string, string> = {
    // All 50 states + DC
    'AL': '(334) 206-5175',
    'AK': '(907) 334-2483',
    'AZ': '(602) 364-2536',
    'AR': '(501) 661-2201',
    'CA': '(916) 651-8848',
    'CO': '(303) 692-2800',
    'CT': '(860) 509-7400',
    'DE': '(302) 292-3930',
    'FL': '(850) 412-4403',
    'GA': '(404) 657-5700',
    'HI': '(808) 733-9010',
    'ID': '(208) 334-6626',
    'IL': '(217) 782-2913',
    'IN': '(317) 233-7474',
    'IA': '(515) 281-7689',
    'KS': '(785) 296-1240',
    'KY': '(502) 564-2800',
    'LA': '(225) 342-9500',
    'ME': '(207) 287-9300',
    'MD': '(410) 402-8000',
    'MA': '(617) 624-6000',
    'MI': '(517) 335-1980',
    'MN': '(651) 201-4101',
    'MS': '(601) 576-7400',
    'MO': '(573) 751-6400',
    'MT': '(406) 444-2037',
    'NE': '(402) 471-3121',
    'NV': '(702) 486-6515',
    'NH': '(603) 271-9039',
    'NJ': '(609) 292-5960',
    'NM': '(505) 476-9025',
    'NY': '(518) 402-1004',
    'NC': '(919) 855-4620',
    'ND': '(701) 328-2352',
    'OH': '(614) 466-7857',
    'OK': '(405) 271-6868',
    'OR': '(971) 673-0540',
    'PA': '(717) 783-1379',
    'RI': '(401) 222-5960',
    'SC': '(803) 545-4370',
    'SD': '(605) 773-3361',
    'TN': '(615) 741-7221',
    'TX': '(512) 438-2200',
    'UT': '(801) 538-6101',
    'VT': '(802) 863-7280',
    'VA': '(804) 367-2106',
    'WA': '(360) 236-4700',
    'WV': '(304) 558-0050',
    'WI': '(608) 266-8024',
    'WY': '(307) 777-7656',
    'DC': '(202) 442-5955',
  };
  return phones[stateCode];
}

/**
 * Get the appropriate regulation code based on category for a state
 */
function getRegulationCodeForCategory(stateCode: string, categoryId: string): string {
  const stateData = getStateByCode(stateCode);
  if (!stateData) return '';

  const refs = stateData.regulatoryReferences;

  // Map category to appropriate regulation reference
  switch (categoryId) {
    case 'A': // Licensing & Operations
      return refs.licensing || refs.hcoLicensing || refs.hhaLicensing || Object.values(refs)[0] || '';
    case 'B': // Personnel Files
      return refs.operations || refs.licensing || Object.values(refs)[0] || '';
    case 'C': // Background Screening
      return refs.backgroundScreening || refs.caregiverScreening || refs.licensing || '';
    case 'D': // Health & Safety
      return refs.operations || refs.licensing || '';
    case 'E': // Training & Competency
      return refs.training || refs.operations || refs.licensing || '';
    case 'F': // Care Delivery
      return refs.operations || refs.hcssLicensing || refs.licensing || '';
    case 'G': // Documentation
      return refs.operations || refs.licensing || '';
    default:
      return Object.values(refs)[0] || '';
  }
}

// ============================================
// TEMPLATE HYDRATION
// ============================================

/**
 * Replace placeholders in a string with state-specific values
 */
function hydratePlaceholders(text: string, state: StateRequirements): string {
  // Get full state data for additional fields
  const stateData = getStateByCode(state.stateCode);

  let result = text
    // Basic state info
    .replace(/\{state\}/g, state.stateName)
    .replace(/\{stateCode\}/g, state.stateCode)
    .replace(/\{agency\}/g, state.agencyName)
    .replace(/\{agencyAbbr\}/g, state.agencyAbbr)
    .replace(/\{agencyUrl\}/g, state.agencyUrl || '')
    .replace(/\{phone\}/g, state.agencyPhone ? `: ${state.agencyPhone}` : '')
    .replace(/\{phoneNumber\}/g, state.agencyPhone || 'your state licensing board')

    // License info
    .replace(/\{retentionYears\}/g, String(state.recordRetentionYears))
    .replace(/\{warningDays\}/g, String(state.licenseRenewalWarningDays))
    .replace(/\{renewalYears\}/g, String(state.licenseRenewalYears))
    .replace(/\{renewalPeriod\}/g, state.licenseRenewalYears === 1 ? 'annually' : `every ${state.licenseRenewalYears} years`)

    // Training
    .replace(/\{annualTrainingHours\}/g, String(state.annualTrainingHours))
    .replace(/\{entryLevelTrainingHours\}/g, String(state.entryLevelTrainingHours))
    .replace(/\{initialTrainingHours\}/g, String(state.entryLevelTrainingHours))

    // Insurance
    .replace(/\{insuranceMinimum\}/g, state.liabilityMinimum)
    .replace(/\{liabilityMinimum\}/g, state.liabilityMinimum)

    // Background checks
    .replace(/\{backgroundFrequency\}/g, String(state.backgroundCheckFrequencyYears))
    .replace(/\{backgroundFrequencyText\}/g, state.backgroundCheckFrequencyYears === 1 ? 'annually' : `every ${state.backgroundCheckFrequencyYears} years`)
    .replace(/\{backgroundCheckType\}/g, state.backgroundCheckType === 'both' ? 'state and FBI' : 'state')

    // Registry info
    .replace(/\{registryName\}/g, state.registryName || 'state aide registry')
    .replace(/\{registryUrl\}/g, state.registryUrl || '');

  // Add TB frequency if required
  if (state.tbTestRequired && state.tbTestFrequencyYears) {
    result = result.replace(/\{tbFrequency\}/g, String(state.tbTestFrequencyYears));
  }

  // Add state-specific license types
  if (stateData?.licenseTypes) {
    result = result.replace(/\{licenseType\}/g, stateData.licenseTypes[0] || 'Home Health Agency');
  }

  // Add registry checks list
  if (stateData?.backgroundCheck.registryChecks) {
    const registries = stateData.backgroundCheck.registryChecks.filter(r => r !== 'OIG').join(', ');
    result = result.replace(/\{registryChecks\}/g, registries);
  }

  // Add special training requirements
  if (stateData?.training.specialRequirements) {
    result = result.replace(/\{specialTraining\}/g, stateData.training.specialRequirements.join(', '));
  }

  return result;
}

/**
 * Hydrate a sub-question with state-specific values
 */
function hydrateSubQuestion(question: SubQuestion, state: StateRequirements): SubQuestion {
  return {
    ...question,
    question: hydratePlaceholders(question.question, state),
    helpText: question.helpText ? hydratePlaceholders(question.helpText, state) : undefined,
    options: question.options?.map(opt => ({
      ...opt,
      label: hydratePlaceholders(opt.label, state),
      description: opt.description ? hydratePlaceholders(opt.description, state) : undefined,
    })),
  };
}

/**
 * Hydrate report language with state-specific values
 */
function hydrateReportLanguage(lang: ReportLanguage, state: StateRequirements): ReportLanguage {
  return {
    compliant: hydratePlaceholders(lang.compliant, state),
    atRisk: hydratePlaceholders(lang.atRisk, state),
    nonCompliant: hydratePlaceholders(lang.nonCompliant, state),
    unknown: hydratePlaceholders(lang.unknown, state),
  };
}

/**
 * Generate a diagnostic question config from a template and state requirements
 */
function generateQuestionFromTemplate(
  template: QuestionTemplate,
  state: StateRequirements
): DiagnosticQuestionConfig {
  // Check for state-specific overrides
  const stateOverride = template.stateOverrides?.[state.stateCode];

  // Merge template with state overrides
  const questions = stateOverride?.questions || template.questions;
  const scoringRules = stateOverride?.scoringRules || template.scoringRules;
  const reportLanguage = stateOverride?.reportLanguage || template.reportLanguage;

  // Extract category from itemId (e.g., "A1" -> "A", "B10" -> "B")
  const categoryId = template.itemId.match(/^([A-G])/)?.[1] || '';

  // Get the category-specific regulatory reference for this state
  const regulationCode = getRegulationCodeForCategory(state.stateCode, categoryId) || state.primaryRegulation;

  // Get state data for additional context
  const stateData = getStateByCode(state.stateCode);

  return {
    id: `${state.stateCode}-${template.itemId}`,
    itemId: template.itemId,
    stateCode: state.stateCode,

    title: hydratePlaceholders(template.title, state),
    description: template.description ? hydratePlaceholders(template.description, state) : '',
    severity: template.severity,

    regulationCode,
    regulationName: state.regulationName,
    agencyName: state.agencyName,
    agencyAbbr: state.agencyAbbr,
    agencyPhone: state.agencyPhone,
    agencyUrl: state.agencyUrl,

    questions: questions.map(q => hydrateSubQuestion(q, state)),
    scoringRules,
    reportLanguage: hydrateReportLanguage(reportLanguage, state),

    stateNotes: stateData?.keyRegulations,
  };
}

// ============================================
// QUESTION GENERATION
// ============================================

/**
 * Generate all diagnostic questions for a state
 * Uses state-specific question banks when available, falls back to templates
 */
export function generateDiagnosticQuestionsForState(stateCode: string): DiagnosticQuestionConfig[] {
  // Check for state-specific question bank first (no placeholders, 100% state-specific)
  if (hasStateSpecificQuestions(stateCode)) {
    const stateQuestions = getStateSpecificQuestions(stateCode);
    if (stateQuestions && stateQuestions.length > 0) {
      return stateQuestions;
    }
  }

  // Fall back to template-based generation for states without specific question banks
  const state = extractStateRequirements(stateCode);
  if (!state) {
    console.warn(`Unknown state code: ${stateCode}`);
    return [];
  }

  const templates = getTemplatesForState(stateCode);
  return templates.map(template => generateQuestionFromTemplate(template, state));
}

/**
 * Get a specific diagnostic question by ID
 */
export function getDiagnosticQuestionById(
  questionId: string,
  stateCode: string
): DiagnosticQuestionConfig | undefined {
  const questions = generateDiagnosticQuestionsForState(stateCode);
  return questions.find(q => q.id === questionId);
}

/**
 * Get diagnostic question by base item ID (without state prefix)
 */
export function getDiagnosticQuestionByItemId(
  itemId: string,
  stateCode: string
): DiagnosticQuestionConfig | undefined {
  const questions = generateDiagnosticQuestionsForState(stateCode);
  return questions.find(q => q.itemId === itemId || q.id === `${stateCode}-${itemId}`);
}

/**
 * Check if a checklist item has a diagnostic question
 */
export function hasDiagnosticQuestion(itemId: string, stateCode: string): boolean {
  // Remove state prefix if present
  const baseId = itemId.includes('-') ? itemId.split('-').slice(1).join('-') : itemId;
  const question = getDiagnosticQuestionByItemId(baseId, stateCode);
  return question !== undefined;
}

/**
 * Get all diagnostic question IDs for a state
 */
export function getDiagnosticQuestionIds(stateCode: string): string[] {
  const questions = generateDiagnosticQuestionsForState(stateCode);
  return questions.map(q => q.id);
}

// ============================================
// SCORING EVALUATION
// ============================================

import type { ChecklistStatus } from '../../types';
import type { SubQuestionAnswer, EvaluationResult } from '../../types/diagnostic';

/**
 * Evaluate answers and determine compliance status
 *
 * Priority order: NON_COMPLIANT > AT_RISK > COMPLIANT
 * This ensures critical failures are caught first.
 *
 * Within each result type, original rule order is preserved
 * (important for daysFuture/daysPast rules where 60 > 30 > 0)
 */
export function evaluateDiagnosticAnswers(
  question: DiagnosticQuestionConfig,
  answers: SubQuestionAnswer[]
): EvaluationResult {
  const answerMap = new Map(answers.map(a => [a.questionId, a.value]));

  // Track missing items for checklists
  const missingItems: string[] = [];

  // Helper to check if a condition (or nested conditions) contains order-dependent types
  const ORDER_DEPENDENT_CONDITIONS = ['daysFuture', 'daysPast', 'countAtLeast', 'countLessThan', 'countBetween'];
  const hasOrderDependentCondition = (rule: ScoringRule): boolean => {
    if (ORDER_DEPENDENT_CONDITIONS.includes(rule.condition)) {
      return true;
    }
    // Check nested conditions in allTrue/anyTrue/anyFalse
    if ((rule.condition === 'allTrue' || rule.condition === 'anyTrue' || rule.condition === 'anyFalse') && Array.isArray(rule.value)) {
      return (rule.value as NestedCondition[]).some((nested) => ORDER_DEPENDENT_CONDITIONS.includes(nested.condition));
    }
    return false;
  };

  // Check if any rules use conditions where original order matters (including nested)
  const hasOrderDependentRules = question.scoringRules.some(hasOrderDependentCondition);

  let rulesToEvaluate: typeof question.scoringRules;

  if (hasOrderDependentRules) {
    // For order-dependent rules, preserve original config order
    // Config should define rules from most restrictive to least
    rulesToEvaluate = question.scoringRules;
  } else {
    // For other rules, group by result type to ensure NON_COMPLIANT is checked first
    const nonCompliantRules = question.scoringRules.filter(r => r.result === 'NON_COMPLIANT');
    const atRiskRules = question.scoringRules.filter(r => r.result === 'AT_RISK');
    const compliantRules = question.scoringRules.filter(r => r.result === 'COMPLIANT');
    const otherRules = question.scoringRules.filter(r =>
      !['NON_COMPLIANT', 'AT_RISK', 'COMPLIANT'].includes(r.result)
    );
    rulesToEvaluate = [...nonCompliantRules, ...atRiskRules, ...compliantRules, ...otherRules];
  }

  for (const rule of rulesToEvaluate) {
    const result = evaluateRule(rule, answerMap, question, missingItems);
    if (result !== null) {
      // Generate report text with missing items filled in
      let reportText = question.reportLanguage?.[result.toLowerCase() as keyof ReportLanguage] || '';
      if (reportText && missingItems.length > 0) {
        reportText = reportText.replace(/\{missing\}/g, missingItems.join(', '));
      }

      return {
        status: result,
        reportText,
        missingItems: missingItems.length > 0 ? missingItems : undefined,
      };
    }
  }

  // Default to UNKNOWN if no rules matched
  return {
    status: 'UNKNOWN',
    reportText: question.reportLanguage.unknown,
  };
}

/**
 * Evaluate a single condition (used for nested conditions in allTrue/anyTrue)
 */
function evaluateCondition(
  cond: NestedCondition,
  answers: Map<string, unknown>,
  question: DiagnosticQuestionConfig,
  missingItems: string[]
): boolean {
  const { condition, value, questionId } = cond;
  const targetQuestionId = questionId || question.questions[0]?.id;
  const answer = answers.get(targetQuestionId);

  switch (condition) {
    case 'equals':
      return answer === value;
    case 'notEquals':
      return answer !== value;
    case 'greaterThan':
      return typeof answer === 'number' && answer > (value as number);
    case 'lessThan':
      return typeof answer === 'number' && answer < (value as number);
    case 'greaterOrEqual':
      return typeof answer === 'number' && answer >= (value as number);
    case 'lessOrEqual':
      return typeof answer === 'number' && answer <= (value as number);
    case 'includes':
      if (Array.isArray(value)) {
        return (value as string[]).includes(answer as string);
      }
      if (Array.isArray(answer)) {
        return answer.includes(value as string);
      }
      return false;
    case 'countAtLeast':
      if (Array.isArray(answer)) {
        // Track missing items for checklist questions
        const targetQuestion = question.questions.find(q => q.id === targetQuestionId);
        if (targetQuestion?.options) {
          const selectedValues = answer as string[];
          targetQuestion.options.forEach(opt => {
            if (!selectedValues.includes(opt.value)) {
              missingItems.push(opt.label);
            }
          });
        }
        return answer.length >= (value as number);
      }
      return false;
    case 'countLessThan':
      return Array.isArray(answer) && answer.length < (value as number);
    case 'daysFuture':
      if (typeof answer === 'string') {
        const answerDate = new Date(answer);
        const today = new Date();
        const diffDays = Math.ceil((answerDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays >= (value as number);
      }
      return false;
    case 'daysPast':
      if (typeof answer === 'string') {
        const answerDate = new Date(answer);
        const today = new Date();
        const diffDays = Math.ceil((today.getTime() - answerDate.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays >= (value as number);
      }
      return false;
    default:
      return false;
  }
}

function evaluateRule(
  rule: ScoringRule,
  answers: Map<string, unknown>,
  question: DiagnosticQuestionConfig,
  missingItems: string[]
): ChecklistStatus | null {
  const { condition, value, questionId } = rule;

  // Get the answer for the specified question (or first question if not specified)
  const targetQuestionId = questionId || question.questions[0]?.id;
  const answer = answers.get(targetQuestionId);

  switch (condition) {
    case 'equals':
      return answer === value ? rule.result : null;

    case 'notEquals':
      return answer !== value ? rule.result : null;

    case 'greaterThan':
      return typeof answer === 'number' && answer > (value as number) ? rule.result : null;

    case 'lessThan':
      return typeof answer === 'number' && answer < (value as number) ? rule.result : null;

    case 'greaterOrEqual':
      return typeof answer === 'number' && answer >= (value as number) ? rule.result : null;

    case 'lessOrEqual':
      return typeof answer === 'number' && answer <= (value as number) ? rule.result : null;

    case 'includes':
      if (Array.isArray(value) && typeof value[0] === 'string') {
        return (value as string[]).includes(answer as string) ? rule.result : null;
      }
      if (Array.isArray(answer)) {
        return answer.includes(value as string) ? rule.result : null;
      }
      return null;

    case 'countAtLeast':
      if (Array.isArray(answer)) {
        // Track missing items for checklist questions
        const targetQuestion = question.questions.find(q => q.id === targetQuestionId);
        if (targetQuestion?.options) {
          const selectedValues = answer as string[];
          targetQuestion.options.forEach(opt => {
            if (!selectedValues.includes(opt.value)) {
              missingItems.push(opt.label);
            }
          });
        }
        return answer.length >= (value as number) ? rule.result : null;
      }
      return null;

    case 'countLessThan':
      if (Array.isArray(answer)) {
        return answer.length < (value as number) ? rule.result : null;
      }
      return null;

    case 'daysFuture':
      if (typeof answer === 'string') {
        const answerDate = new Date(answer);
        const today = new Date();
        const diffDays = Math.ceil((answerDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays >= (value as number) ? rule.result : null;
      }
      return null;

    case 'daysPast':
      if (typeof answer === 'string') {
        const answerDate = new Date(answer);
        const today = new Date();
        const diffDays = Math.ceil((today.getTime() - answerDate.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays >= (value as number) ? rule.result : null;
      }
      return null;

    case 'allTrue':
      // Evaluate nested conditions - all must be true
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'condition' in value[0]) {
        const allMatch = (value as NestedCondition[]).every(subCondition =>
          evaluateCondition(subCondition, answers, question, missingItems)
        );
        return allMatch ? rule.result : null;
      }
      return null;

    case 'anyTrue':
      // Evaluate nested conditions - any must be true
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'condition' in value[0]) {
        const anyMatch = (value as NestedCondition[]).some(subCondition =>
          evaluateCondition(subCondition, answers, question, missingItems)
        );
        return anyMatch ? rule.result : null;
      }
      return null;

    default:
      return null;
  }
}

// ============================================
// EXPORTS
// ============================================

export {
  type StateRequirements,
  type DiagnosticQuestionConfig,
};
